import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  "https://lgdvohwzgdzczftsnwbp.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxnZHZvaHd6Z2R6Y3pmdHNud2JwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzkxMjE4NzYsImV4cCI6MjA5NDY5Nzg3Nn0.JVhoCGChYbUMvRwMwrOVmw2Vh8Zko6IMWk0W-V0CsD4"
);

// ─── RÔLES ────────────────────────────────────────────────────────────────────
const ROLES_CONFIG = {
  admin: { label: "Administrateur", color: "#2563eb" },       // invisible
  managervp: { label: "Manager VP", color: "#dc2626" },       // vous
  manager: { label: "Manager", color: "#7c3aed" },            // leur manager
  employee: { label: "Employé", color: "#059669" },           // tout le monde
};

// Rôles visibles dans les cases à cocher (admin exclu)
const ROLES_VISIBLE = [
  { value: "managervp", label: "🎯 Manager VP" },
  { value: "manager", label: "👥 Manager" },
  { value: "employee", label: "👤 Employé" },
];

const STATUTS_DEVIS = [
  { value: "nouveau", label: "🆕 Nouveau", color: "#6366f1" },
  { value: "envoyé", label: "📤 Envoyé", color: "#0891b2" },
  { value: "en attente", label: "⏳ En attente", color: "#f59e0b" },
  { value: "accepté", label: "✅ Accepté", color: "#22c55e" },
  { value: "refusé", label: "❌ Refusé", color: "#ef4444" },
  { value: "dossier en cours", label: "📂 Dossier en cours", color: "#8b5cf6" },
  { value: "dossier traité", label: "✔️ Dossier traité", color: "#059669" },
];

const FILE_ICONS = {
  pdf: { icon: "📄", color: "#ef4444" }, xlsx: { icon: "📊", color: "#22c55e" },
  xls: { icon: "📊", color: "#22c55e" }, docx: { icon: "📝", color: "#3b82f6" },
  doc: { icon: "📝", color: "#3b82f6" }, pptx: { icon: "📽️", color: "#f97316" },
  ppt: { icon: "📽️", color: "#f97316" }, png: { icon: "🖼️", color: "#a855f7" },
  jpg: { icon: "🖼️", color: "#a855f7" }, jpeg: { icon: "🖼️", color: "#a855f7" },
  txt: { icon: "📃", color: "#6b7280" }, other: { icon: "📁", color: "#6b7280" },
};

const FOLDERS = ["Devis", "Contrats", "Finance", "Rapports", "Planning", "RH", "Commercial"];
const COLORS_AVATAR = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2", "#db2777", "#0d9488"];
const PRIORITY_COLORS = { haute: "#ef4444", moyenne: "#f59e0b", basse: "#22c55e" };
const TASK_STATUS_COLORS = { "à faire": "#6b7280", "en cours": "#3b82f6", "terminé": "#22c55e" };

const getStatut = (val) => STATUTS_DEVIS.find(s => s.value === val) || { label: val, color: "#6b7280" };
const getExt = (name) => name.split(".").pop().toLowerCase();
const formatSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
const getInitials = (name) => name ? name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) : "??";

// ─── UI ───────────────────────────────────────────────────────────────────────
const Avatar = ({ user, size = 36 }) => {
  if (user.photo) return <img src={user.photo} alt={user.name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid rgba(255,255,255,0.3)" }} onError={e => e.target.style.display = "none"} />;
  return <div style={{ width: size, height: size, borderRadius: "50%", background: user.color || "#2563eb", color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, flexShrink: 0, border: "2px solid rgba(255,255,255,0.3)" }}>{user.avatar || getInitials(user.name)}</div>;
};

const Badge = ({ label, color }) => <span style={{ background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>;
const Card = ({ children, style = {} }) => <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", ...style }}>{children}</div>;

const Btn = ({ children, onClick, variant = "primary", small = false, disabled = false, style = {} }) => {
  const styles = { primary: { background: "#1e3a5f", color: "#fff", border: "none" }, secondary: { background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0" }, danger: { background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }, ghost: { background: "transparent", color: "#64748b", border: "1px solid transparent" }, warning: { background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }, success: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" } };
  return <button onClick={onClick} disabled={disabled} style={{ ...styles[variant], padding: small ? "5px 12px" : "9px 20px", borderRadius: 8, fontWeight: 600, fontSize: small ? 12 : 14, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: 6, ...style }}>{children}</button>;
};

const Input = ({ label, value, onChange, type = "text", placeholder = "", required = false, onKeyDown }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    {label && <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}{required && " *"}</label>}
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} onKeyDown={onKeyDown} style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none", background: "#fafafa", color: "#111" }} />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    {label && <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}</label>}
    <select value={value} onChange={e => onChange(e.target.value)} style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, background: "#fafafa", color: "#111" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, minWidth: 360, maxWidth: 560, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const RoleCheckboxes = ({ label, selected, onChange, color = "#2563eb" }) => (
  <div>
    <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 13, color: "#374151" }}>{label}</p>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {ROLES_VISIBLE.map(r => (
        <label key={r.value} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "6px 12px", borderRadius: 8, border: `1px solid ${selected.includes(r.value) ? color : "#e2e8f0"}`, background: selected.includes(r.value) ? color + "15" : "#fafafa" }}>
          <input type="checkbox" checked={selected.includes(r.value)} onChange={e => onChange(e.target.checked ? [...selected, r.value] : selected.filter(x => x !== r.value))} />
          <span style={{ fontSize: 12, fontWeight: 600, color: selected.includes(r.value) ? color : "#374151" }}>{r.label}</span>
        </label>
      ))}
    </div>
  </div>
);

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [page, setPage] = useState("dashboard");
  const [profiles, setProfiles] = useState([]);
  const [devis, setDevis] = useState([]);
    
  const [files, setFiles] = useState([]);
  const [filesCorbeille, setFilesCorbeille] = useState([]);
  const [adminArchive, setAdminArchive] = useState([]); // Corbeille secrète admin
  const [historique, setHistorique] = useState([]); // Journal d'activité
  const [objectifs, setObjectifs] = useState({ mensuel: 0, trimestriel: 0, semestriel: 0, annuel: 0 });
  const [tasks, setTasks] = useState([]);
    
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [annonce, setAnnonce] = useState("");
  const [messages, setMessages] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [rappelAlerte, setRappelAlerte] = useState(null);
  const [showAnnonce, setShowAnnonce] = useState(false);
  const [annonceActive, setAnnonceActive] = useState(null);

  const notify = (msg, type = "success") => { setNotification({ msg, type }); setTimeout(() => setNotification(null), 3500); };

  const addLog = async (action, detail, currentProfile) => {
    // Admin ne laisse pas de trace
    if (!currentProfile || currentProfile.role === "admin") return;
    const entry = {
      user_id: currentProfile.id,
      user_name: currentProfile.name,
      user_role: currentProfile.role,
      action,
      detail,
    };
    // Sauvegarder dans Supabase
    await supabase.from("historique").insert(entry);
    // Recharger l'historique
    loadHistorique();
  };

const loadRappels = async () => {
  const { data } = await supabase.from("rappels").select("*").eq("done", false).order("date_rappel", { ascending: true });
  if (data) {
    setRappels(data);
    // Vérifier si un rappel est pour aujourd'hui
    const today = new Date().toDateString();
    const alerteAujourdhui = data.find(r => new Date(r.date_rappel).toDateString() === today);
    if (alerteAujourdhui) setRappelAlerte(alerteAujourdhui);
  }
};

const loadFiles = async () => {
  const { data } = await supabase.from("fichiers").select("*").order("created_at", { ascending: false });
  if (data) {
    setFiles(data.map(f => ({
      id: f.id,
      name: f.name,
      path: f.path,
      folder: f.folder,
      size: f.size,
      type: f.type,
      uploadedBy: f.uploaded_by,
      date: f.date,
      allowedRoles: f.allowed_roles ? JSON.parse(f.allowed_roles) : ["managervp", "manager", "employee"],
      downloadRoles: f.download_roles ? JSON.parse(f.download_roles) : ["managervp", "manager", "employee"],
    })));
  }
};

const loadMessages = async () => {
  const { data } = await supabase.from("messages").select("*").order("created_at", { ascending: true }).limit(100);
  if (data) setMessages(data);
}; 
const loadAnnonce = async () => {
  const { data } = await supabase.from("annonces").select("*").eq("active", true).order("created_at", { ascending: false }).limit(1);
  if (data && data.length > 0) setAnnonceActive(data[0]);
};

  const loadHistorique = async () => {
    const { data } = await supabase.from("historique").select("*").order("created_at", { ascending: false }).limit(200);
    if (data) {
      setHistorique(data.map(h => ({
        id: h.id,
        userId: h.user_id,
        userName: h.user_name,
        userRole: h.user_role,
        action: h.action,
        detail: h.detail,
        date: h.created_at,
      })));
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
      else { setProfile(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
      if (data) {
        setProfile(data);
      } else {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) { setLoading(false); return; }
        const email = userData.user.email || "";
        const newProfile = { id: userId, name: email.split("@")[0] || "Utilisateur", email: email, role: "employee", color: "#2563eb", avatar: email.slice(0, 2).toUpperCase() || "U", poste: "", phone: "", photo: "", active: true };
        await supabase.from("profiles").insert(newProfile);
        setProfile(newProfile);
      }
      loadAllProfiles();
      loadRappels();
      loadFiles();
      loadHistorique();
      loadAnnonce();
      loadMessages();
    } catch (err) {
      console.error("Erreur loadProfile:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllProfiles = async () => {
    const { data } = await supabase.from("profiles").select("*");
    if (data) setProfiles(data);
  };

  const handleLogin = async () => {
  setLoginError("");
  const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });
  if (error) { setLoginError("Email ou mot de passe incorrect."); return; }
  const dismissed = localStorage.getItem("welcome_dismissed_" + loginEmail);
  if (!dismissed) setShowWelcome(true);
};

  const handleLogout = async () => { await supabase.auth.signOut(); setSession(null); setProfile(null); };
  const updateProfile = async (updated) => { await supabase.from("profiles").upsert(updated); setProfile(updated); loadAllProfiles(); };

  if (loading) return <div style={{ minHeight: "100vh", background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ color: "#fff", fontSize: 18 }}>⏳ Chargement...</div></div>;

  if (!session || !profile) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;600&display=swap'); *{box-sizing:border-box;font-family:'DM Sans',sans-serif;}`}</style>
        <div style={{ background: "rgba(255,255,255,0.05)", backdropFilter: "blur(20px)", borderRadius: 20, padding: "40px 44px", width: 420, border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 30px 80px rgba(0,0,0,0.4)" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <img src="https://central.aubenet.fr/logo.png" alt="Logo" style={{ width: 80, height: 80, objectFit: "contain", marginBottom: 8 }} />
            <h1 style={{ color: "#fff", margin: 0, fontSize: 28, fontWeight: 700 }}>WorkSpace Central</h1>
            <p style={{ color: "rgba(255,255,255,0.5)", margin: "6px 0 0", fontSize: 14 }}>Espace de travail collaboratif</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Adresse email</label>
              <input value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="votre@email.fr" type="email" onKeyDown={e => e.key === "Enter" && handleLogin()} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Mot de passe</label>
              <input value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="••••••••" type="password" onKeyDown={e => e.key === "Enter" && handleLogin()} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", outline: "none", boxSizing: "border-box" }} />
            </div>
            {loginError && <p style={{ color: "#f87171", fontSize: 13, margin: 0 }}>{loginError}</p>}
            <button onClick={handleLogin} style={{ padding: "12px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 4 }}>Se connecter</button>
          </div>
          <div style={{ marginTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, textAlign: "center" }}>
            <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 11, margin: 0 }}>© 2026 WorkSpace Central — Tous droits réservés</p>
          </div>
        </div>
      </div>
    );
  }

  const isAdmin = profile.role === "admin";
  const isManagerVP = profile.role === "managervp";
  const isManager = ["admin", "managervp", "manager"].includes(profile.role);
  const canManageUsers = ["admin", "managervp"].includes(profile.role);
  const roleConfig = ROLES_CONFIG[profile.role] || ROLES_CONFIG.employee;

  const NAV = [
    { id: "dashboard", label: "Tableau de bord", icon: "📊" },
    { id: "devis", label: "Devis", icon: "📋" },
    { id: "files", label: "Fichiers", icon: "🗂️" },
    { id: "tasks", label: "Tâches", icon: "✅" },
    ...(canManageUsers ? [{ id: "users", label: "Utilisateurs", icon: "👥" }] : []),
    ...(canManageUsers ? [{ id: "historique", label: "Historique", icon: "📜" }] : []),
    ...(canManageUsers ? [{ id: "annonces", label: "Annonces", icon: "📢" }] : []),
    { id: "rappels", label: "Rappels", icon: "🔔" },
    { id: "chat", label: "Chat Équipe", icon: "💬" },
    { id: "profil", label: "Mon Profil", icon: "👤" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: "#f8fafc" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;600&display=swap'); *{box-sizing:border-box;font-family:'DM Sans',sans-serif;} ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:10px;}`}</style>

      {notification && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, background: notification.type === "success" ? "#22c55e" : notification.type === "warning" ? "#f59e0b" : "#ef4444", color: "#fff", padding: "12px 20px", borderRadius: 10, fontWeight: 600, fontSize: 14, boxShadow: "0 8px 20px rgba(0,0,0,0.15)" }}>{notification.msg}</div>}

{annonceActive && (
  <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1999, background: "#1e3a5f", color: "#fff", padding: "14px 24px", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.3)", maxWidth: 500, width: "90%", display: "flex", alignItems: "center", gap: 12 }}>
    <span style={{ fontSize: 20 }}>📢</span>
    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, flex: 1 }}>{annonceActive.message}</p>
    <button onClick={() => setAnnonceActive(null)} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 24, height: 24, cursor: "pointer", fontSize: 14 }}>✕</button>
  </div>
)}

{rappelAlerte && (
  <div style={{ position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)", zIndex: 1998, background: "#dc2626", color: "#fff", padding: "14px 24px", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.3)", maxWidth: 500, width: "90%", display: "flex", alignItems: "center", gap: 12 }}>
    <span style={{ fontSize: 20 }}>🔔</span>
    <div style={{ flex: 1 }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Rappel aujourd'hui : {rappelAlerte.titre}</p>
      {rappelAlerte.adresse && <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.9 }}>📍 {rappelAlerte.adresse}</p>}
    </div>
    <button onClick={() => setRappelAlerte(null)} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 24, height: 24, cursor: "pointer", fontSize: 14 }}>✕</button>
  </div>
)}

{showWelcome && (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
    <div style={{ background: "#fff", borderRadius: 20, padding: 36, maxWidth: 480, width: "100%", boxShadow: "0 25px 60px rgba(0,0,0,0.3)", textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}><img src="https://central.aubenet.fr/logo.png" alt="Logo" style={{ width: 80, height: 80, objectFit: "contain" }} /></div>
      <h2 style={{ margin: "0 0 16px", fontSize: 22, fontWeight: 700, color: "#0f172a" }}>Bienvenue sur WorkSpace Central</h2>
      <div style={{ background: "#f8fafc", borderRadius: 12, padding: "16px 20px", marginBottom: 24, border: "1px solid #e2e8f0" }}>
        <p style={{ margin: 0, fontSize: 15, color: "#374151", lineHeight: 1.6 }}>
          Ce logiciel a été conçu spécialement pour nous. Il vaut plusieurs milliers d'euros sur le marché. Je l'ai fait pour qu'on travaille mieux ensemble. <strong>Respectez-le et utilisez-le bien.</strong>
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={() => setShowWelcome(false)} style={{ padding: "12px 24px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
          ✅ Compris, allons travailler !
        </button>
        <button onClick={() => { localStorage.setItem("welcome_dismissed_" + profile?.email, "true"); setShowWelcome(false); }} style={{ padding: "10px 24px", background: "transparent", color: "#94a3b8", border: "1px solid #e2e8f0", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          Ne plus afficher ce message
        </button>
      </div>
    </div>
  </div>
)}

      {/* SIDEBAR */}
      <div style={{ width: sidebarOpen ? 240 : 64, background: "#0f172a", flexShrink: 0, display: "flex", flexDirection: "column", transition: "width 0.2s", overflow: "hidden" }}>
        <div style={{ padding: "20px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}><img src="https://central.aubenet.fr/logo.png" alt="Logo" style={{ width: 22, height: 22, objectFit: "contain" }} /></span>
          {sidebarOpen && <span style={{ color: "#fff", fontWeight: 700, fontSize: 16, whiteSpace: "nowrap" }}>WorkSpace Central</span>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.08)", border: "none", color: "#94a3b8", borderRadius: 6, width: 28, height: 28, cursor: "pointer", flexShrink: 0 }}>{sidebarOpen ? "◀" : "▶"}</button>
        </div>
        <nav style={{ flex: 1, padding: "12px 8px" }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => setPage(n.id)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", background: page === n.id ? "#2563eb" : "transparent", color: page === n.id ? "#fff" : "#94a3b8", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 4, fontWeight: 600, fontSize: 14, transition: "all 0.15s", whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{n.icon}</span>
              {sidebarOpen && n.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 12px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 10 }} onClick={() => setPage("profil")}>
            <Avatar user={profile} size={32} />
            {sidebarOpen && <div style={{ flex: 1, overflow: "hidden" }}><div style={{ color: "#fff", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{profile.name}</div><div style={{ color: "#64748b", fontSize: 11 }}>{profile.poste || roleConfig.label}</div></div>}
          </div>
          {sidebarOpen && <button onClick={handleLogout} style={{ width: "100%", padding: "8px", background: "rgba(239,68,68,0.12)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Déconnexion</button>}
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 28px", background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{NAV.find(n => n.id === page)?.icon} {NAV.find(n => n.id === page)?.label}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Badge label={roleConfig.label} color={roleConfig.color} />
            <div onClick={() => setPage("profil")} style={{ cursor: "pointer" }}><Avatar user={profile} size={36} /></div>
          </div>
        </div>
        <div style={{ flex: 1, padding: "24px 28px", overflow: "auto" }}>
          {page === "dashboard" && <Dashboard profile={profile} profiles={profiles} devis={devis} files={files} tasks={tasks} isManager={isManager} isAdmin={isAdmin} objectifs={objectifs} setObjectifs={setObjectifs} />}
          {page === "devis" && <DevisPage devis={devis} setDevis={setDevis} profiles={profiles} profile={profile} isManager={isManager} notify={notify} addLog={addLog} />}
          {page === "files" && <FilesPage files={files} setFiles={setFiles} filesCorbeille={filesCorbeille} setFilesCorbeille={setFilesCorbeille} adminArchive={adminArchive} setAdminArchive={setAdminArchive} profile={profile} isManager={isManager} isAdmin={isAdmin} notify={notify} addLog={addLog} />}
          {page === "historique" && canManageUsers && <HistoriquePage historique={historique} profiles={profiles} />}
{page === "annonces" && canManageUsers && <AnnoncesPage annonce={annonce} setAnnonce={setAnnonce} annonceActive={annonceActive} setAnnonceActive={setAnnonceActive} profile={profile} notify={notify} />}
{page === "chat" && <ChatPage messages={messages} setMessages={setMessages} profile={profile} isManager={isManager} notify={notify} />}
{page === "rappels" && <RappelsPage rappels={rappels} setRappels={setRappels} profiles={profiles} profile={profile} isManager={isManager} notify={notify} />}
          {page === "tasks" && <TasksPage tasks={tasks} setTasks={setTasks} profiles={profiles} profile={profile} isManager={isManager} notify={notify} addLog={addLog} />}
          {page === "users" && canManageUsers && <UsersPage profiles={profiles} setProfiles={setProfiles} profile={profile} notify={notify} isAdmin={isAdmin} />}
          {page === "profil" && <ProfilPage profile={profile} updateProfile={updateProfile} notify={notify} />}
        </div>
        <div style={{ padding: "12px 28px", background: "#fff", borderTop: "1px solid #e5e7eb", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>© 2026 WorkSpace Central - Conçu par PERCY Vicky - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ profile, profiles, devis, files, tasks, isManager, isAdmin, objectifs, setObjectifs }) {
  const activeDevis = devis.filter(d => !d.archived && !d.deleted);
  const totalCA = activeDevis.filter(d => d.statut === "accepté").reduce((s, d) => s + d.montant, 0);

  const stats = [
    { label: "CA Accepté", value: `${totalCA.toLocaleString("fr-FR")} €`, icon: "💶", color: "#059669" },
    { label: "CA Potentiel", value: `${activeDevis.reduce((s, d) => s + d.montant, 0).toLocaleString("fr-FR")} €`, icon: "📈", color: "#2563eb" },
    { label: "En attente", value: activeDevis.filter(d => d.statut === "en attente").length, icon: "⏳", color: "#f59e0b" },
    { label: "Refusés", value: activeDevis.filter(d => d.statut === "refusé").length, icon: "❌", color: "#ef4444" },
    { label: "Dossiers en cours", value: activeDevis.filter(d => d.statut === "dossier en cours").length, icon: "📂", color: "#8b5cf6" },
    { label: "Fichiers stockés", value: files.length, icon: "🗂️", color: "#0891b2" },
  ];

  // Calcul CA par période
  const now = new Date();
  const caByPeriod = (months) => {
    const since = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
    return activeDevis.filter(d => d.statut === "accepté" && new Date(d.date) >= since).reduce((s, d) => s + d.montant, 0);
  };
  const caMensuel = caByPeriod(1);
  const caTrimestriel = caByPeriod(3);
  const caSemestriel = caByPeriod(6);
  const caAnnuel = caByPeriod(12);

  const [editObj, setEditObj] = useState(false);
  const [tempObj, setTempObj] = useState(objectifs);

  const getColor = (ca, obj) => {
    if (!obj || obj === 0) return "#6b7280";
    const pct = (ca / obj) * 100;
    if (pct >= 100) return "#f59e0b";
    if (pct >= 70) return "#22c55e";
    if (pct >= 30) return "#f97316";
    return "#ef4444";
  };

  const getPct = (ca, obj) => !obj || obj === 0 ? 0 : Math.min((ca / obj) * 100, 100);

  const getEmoji = (ca, obj) => {
    if (!obj || obj === 0) return "📊";
    const pct = (ca / obj) * 100;
    if (pct >= 100) return "🏆";
    if (pct >= 70) return "🟢";
    if (pct >= 30) return "🟡";
    return "🔴";
  };

  const periodes = [
    { key: "mensuel", label: "Mensuel", ca: caMensuel },
    { key: "trimestriel", label: "Trimestriel", ca: caTrimestriel },
    { key: "semestriel", label: "Semestriel", ca: caSemestriel },
    { key: "annuel", label: "Annuel", ca: caAnnuel },
  ];

  const bilan = {};
  activeDevis.forEach(d => {
    const mois = d.date.slice(0, 7);
    if (!bilan[mois]) bilan[mois] = { total: 0, accepté: 0, refusé: 0, count: 0 };
    bilan[mois].total += d.montant; bilan[mois].count += 1;
    if (d.statut === "accepté") bilan[mois].accepté += d.montant;
    if (d.statut === "refusé") bilan[mois].refusé += d.montant;
  });

  // Cacher l'admin de la liste équipe
  const teamVisible = profiles.filter(u => u.active && u.role !== "admin");

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 24 }}>
        {stats.map(s => (
          <Card key={s.label}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div><p style={{ margin: 0, fontSize: 12, color: "#64748b", fontWeight: 500 }}>{s.label}</p><p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Mono', monospace" }}>{s.value}</p></div>
              <span style={{ fontSize: 26 }}>{s.icon}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Objectifs CA */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🎯 Objectifs de CA (devis acceptés)</h3>
          {isManager && (
            <button onClick={() => { setTempObj(objectifs); setEditObj(!editObj); }} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: editObj ? "#1e3a5f" : "#f1f5f9", color: editObj ? "#fff" : "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              {editObj ? "✕ Fermer" : "✏️ Modifier"}
            </button>
          )}
        </div>

        {editObj && isManager && (
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginBottom: 16, border: "1px solid #e2e8f0" }}>
            <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 13, color: "#374151" }}>Définir vos objectifs (€)</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              {periodes.map(p => (
                <div key={p.key}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{p.label}</label>
                  <input type="number" value={tempObj[p.key] || ""} onChange={e => setTempObj({ ...tempObj, [p.key]: parseFloat(e.target.value) || 0 })}
                    placeholder="0" style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
                </div>
              ))}
            </div>
            <button onClick={() => { setObjectifs(tempObj); setEditObj(false); }} style={{ marginTop: 12, padding: "8px 20px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
              💾 Enregistrer
            </button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {periodes.map(p => {
            const obj = objectifs[p.key] || 0;
            const pct = getPct(p.ca, obj);
            const color = getColor(p.ca, obj);
            const emoji = getEmoji(p.ca, obj);
            return (
              <div key={p.key} style={{ padding: "14px 16px", borderRadius: 10, border: `1px solid ${color}33`, background: color + "08" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{emoji} {p.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: color }}>{obj > 0 ? Math.round(pct) + "%" : "—"}</span>
                </div>
                <div style={{ background: "#e5e7eb", borderRadius: 20, height: 8, marginBottom: 8, overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: color, borderRadius: 20, transition: "width 0.5s" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", fontFamily: "'DM Mono', monospace" }}>{p.ca.toLocaleString("fr-FR")} €</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{obj > 0 ? "/ " + obj.toLocaleString("fr-FR") + " €" : "Pas d'objectif"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>📋 Derniers devis</h3>
          {activeDevis.slice(0, 5).map(d => {
            const s = getStatut(d.statut);
            return (
              <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div><p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{d.client}</p><p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>{d.id} · {d.date}</p></div>
                <div style={{ textAlign: "right" }}><p style={{ margin: "0 0 3px", fontWeight: 700, fontSize: 13, fontFamily: "'DM Mono', monospace" }}>{d.montant.toLocaleString("fr-FR")} €</p><Badge label={s.label} color={s.color} /></div>
              </div>
            );
          })}
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>📅 Bilan mensuel</h3>
          {Object.entries(bilan).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 4).map(([mois, data]) => (
            <div key={mois} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontWeight: 600, fontSize: 14 }}>{mois}</span><span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: "#2563eb" }}>{data.total.toLocaleString("fr-FR")} €</span></div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}><Badge label={`✅ ${data.accepté.toLocaleString("fr-FR")} €`} color="#22c55e" /><Badge label={`❌ ${data.refusé.toLocaleString("fr-FR")} €`} color="#ef4444" /><Badge label={`${data.count} devis`} color="#6b7280" /></div>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>👥 Équipe</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {teamVisible.map(u => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fafafa" }}>
              <Avatar user={u} size={42} />
              <div><p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{u.name}</p><p style={{ margin: "2px 0", fontSize: 12, color: "#64748b" }}>{u.poste || ROLES_CONFIG[u.role]?.label}</p><Badge label={ROLES_CONFIG[u.role]?.label || u.role} color={ROLES_CONFIG[u.role]?.color || "#6b7280"} /></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── DEVIS ────────────────────────────────────────────────────────────────────
function DevisPage({ devis, setDevis, profiles, profile, isManager, notify, addLog }) {
  const [view, setView] = useState("actifs");
  const [filterStatut, setFilterStatut] = useState("tous");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmEmpty, setShowConfirmEmpty] = useState(false);
  const [form, setForm] = useState({ client: "", montant: "", description: "", statut: "nouveau" });

  const actifs = devis.filter(d => !d.archived && !d.deleted);
  const archived = devis.filter(d => d.archived && !d.deleted);
  const corbeille = devis.filter(d => d.deleted);
  const displayed = view === "actifs" ? actifs : view === "archivés" ? archived : corbeille;
  const filtered = filterStatut === "tous" ? displayed : displayed.filter(d => d.statut === filterStatut);

  const handleCreate = () => {
    if (!form.client || !form.montant) return;
    const newDevis = { id: `DEV-${String(devis.length + 1).padStart(3, "0")}`, ...form, montant: parseFloat(form.montant), date: new Date().toISOString().split("T")[0], createdBy: profile.id, archived: false, deleted: false };
    setDevis([newDevis, ...devis]);
    setForm({ client: "", montant: "", description: "", statut: "nouveau" });
    setShowModal(false);
    notify("Devis créé !");
    addLog("📋 Devis créé", `${newDevis.id} — ${newDevis.client} — ${newDevis.montant.toLocaleString("fr-FR")} €`, profile);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ id: "actifs", label: `📋 Actifs (${actifs.length})` }, { id: "archivés", label: `📦 Archivés (${archived.length})` }, { id: "corbeille", label: `🗑️ Corbeille (${corbeille.length})` }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid #e2e8f0", background: view === t.id ? "#1e3a5f" : "#fff", color: view === t.id ? "#fff" : "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>{t.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {view === "corbeille" && corbeille.length > 0 && isManager && <Btn variant="danger" small onClick={() => setShowConfirmEmpty(true)}>🗑️ Vider</Btn>}
          {view === "actifs" && <Btn onClick={() => setShowModal(true)}>+ Nouveau devis</Btn>}
        </div>
      </div>
      {view === "actifs" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          <button onClick={() => setFilterStatut("tous")} style={{ padding: "5px 14px", borderRadius: 20, border: "1px solid #e2e8f0", background: filterStatut === "tous" ? "#1e3a5f" : "#fff", color: filterStatut === "tous" ? "#fff" : "#374151", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Tous</button>
          {STATUTS_DEVIS.map(s => (<button key={s.value} onClick={() => setFilterStatut(s.value)} style={{ padding: "5px 14px", borderRadius: 20, border: `1px solid ${s.color}44`, background: filterStatut === s.value ? s.color : s.color + "11", color: filterStatut === s.value ? "#fff" : s.color, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>{s.label}</button>))}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.length === 0 && <Card style={{ textAlign: "center", padding: 40 }}><p style={{ color: "#94a3b8", fontSize: 16 }}>{view === "corbeille" ? "🗑️ Corbeille vide" : view === "archivés" ? "📦 Aucun archivé" : "📋 Aucun devis"}</p></Card>}
        {filtered.map(d => {
          const creator = profiles.find(u => u.id === d.createdBy);
          const s = getStatut(d.statut);
          return (
            <Card key={d.id} style={{ padding: "14px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}><span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#64748b" }}>{d.id}</span><Badge label={s.label} color={s.color} /></div>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{d.client}</h4>
                  <p style={{ margin: "2px 0 0", fontSize: 13, color: "#64748b" }}>{d.description}</p>
                  {creator && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}><Avatar user={creator} size={18} /><span style={{ fontSize: 11, color: "#94a3b8" }}>{creator.name} · {d.date}</span></div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#1e3a5f", fontFamily: "'DM Mono', monospace" }}>{d.montant.toLocaleString("fr-FR")} €</p>
                  {view === "actifs" && isManager && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <Select value={d.statut} onChange={v => { setDevis(devis.map(x => x.id === d.id ? { ...x, statut: v } : x)); notify("Statut mis à jour"); addLog("🔄 Statut devis modifié", `${d.id} — ${d.client} → ${v}`, profile); }} options={STATUTS_DEVIS} />
                      <Btn small variant="warning" onClick={() => { setDevis(devis.map(x => x.id === d.id ? { ...x, archived: true } : x)); notify("Archivé 📦"); }}>📦</Btn>
                      <Btn small variant="ghost" onClick={() => { setDevis(devis.map(x => x.id === d.id ? { ...x, deleted: true, archived: false } : x)); notify("Corbeille 🗑️", "warning"); }}>🗑️</Btn>
                    </div>
                  )}
                  {view === "archivés" && <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}><Btn small variant="success" onClick={() => { setDevis(devis.map(x => x.id === d.id ? { ...x, archived: false } : x)); notify("Restauré ✅"); }}>↩️ Restaurer</Btn><Btn small variant="ghost" onClick={() => { setDevis(devis.map(x => x.id === d.id ? { ...x, deleted: true, archived: false } : x)); notify("Corbeille", "warning"); }}>🗑️</Btn></div>}
                  {view === "corbeille" && <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}><Btn small variant="success" onClick={() => { setDevis(devis.map(x => x.id === d.id ? { ...x, deleted: false } : x)); notify("Restauré ✅"); }}>↩️ Restaurer</Btn><Btn small variant="danger" onClick={() => { setDevis(devis.filter(x => x.id !== d.id)); notify("Supprimé définitivement", "error"); }}>❌ Supprimer</Btn></div>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Nouveau devis">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Client" value={form.client} onChange={v => setForm({ ...form, client: v })} required placeholder="Nom du client ou entreprise" />
          <Input label="Montant (€)" type="number" value={form.montant} onChange={v => setForm({ ...form, montant: v })} required placeholder="0" />
          <Input label="Description / Objet" value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Objet du devis" />
          <Select label="Statut initial" value={form.statut} onChange={v => setForm({ ...form, statut: v })} options={STATUTS_DEVIS} />
          <div style={{ display: "flex", gap: 10 }}><Btn onClick={handleCreate} style={{ flex: 1 }}>Créer le devis</Btn><Btn variant="secondary" onClick={() => setShowModal(false)}>Annuler</Btn></div>
        </div>
      </Modal>
      <Modal open={showConfirmEmpty} onClose={() => setShowConfirmEmpty(false)} title="⚠️ Vider la corbeille">
        <p style={{ color: "#374151", fontSize: 14 }}>Suppression <strong>définitive</strong>. Irréversible.</p>
        <div style={{ display: "flex", gap: 10 }}><Btn variant="danger" onClick={() => { setDevis(devis.filter(d => !d.deleted)); setShowConfirmEmpty(false); notify("Corbeille vidée", "error"); }} style={{ flex: 1 }}>Vider définitivement</Btn><Btn variant="secondary" onClick={() => setShowConfirmEmpty(false)}>Annuler</Btn></div>
      </Modal>
    </div>
  );
}

// ─── FICHIERS ─────────────────────────────────────────────────────────────────
function FilesPage({ files, setFiles, filesCorbeille, setFilesCorbeille, adminArchive, setAdminArchive, profile, isManager, isAdmin, notify, addLog }) {
  const [view, setView] = useState("actifs");
  const [folder, setFolder] = useState("Tous");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewRoles, setViewRoles] = useState(["managervp", "manager", "employee"]);
  const [dlRoles, setDlRoles] = useState(["managervp", "manager", "employee"]);
  const [selectedFolder, setSelectedFolder] = useState("Contrats");
  const fileInputRef = useRef();
  const folderInputRef = useRef();

  // Admin voit TOUT — les autres selon allowedRoles
  const accessible = isAdmin ? files : files.filter(f => (f.allowedRoles || ["managervp", "manager", "employee"]).includes(profile.role));
  const filtered = (folder === "Tous" ? accessible : accessible.filter(f => f.folder === folder))
    .filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

  // Admin télécharge TOUT — les autres selon downloadRoles
  const canDownload = (f) => isAdmin || (f.downloadRoles || ["managervp", "manager", "employee"]).includes(profile.role);

  const handleFileUpload = async (e) => {
    const fileList = Array.from(e.target.files);
    if (fileList.length === 0) return;
    setUploading(true);
    notify(`Upload de ${fileList.length} fichier(s) en cours... ⏳`, "warning");
    let success = 0;
    const newFiles = [];
    for (const file of fileList) {
      try {
 if (!file.type && file.size === 0) continue; // Ignorer les dossiers vides
 if (file.name === file.webkitRelativePath?.split("/")[0]) continue; if (file.name === "desktop.ini" || file.name.startsWith("."))     continue; // Ignorer le dossier racine        
 const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const { error } = await supabase.storage.from("central-fichiers").upload(`${selectedFolder}/${fileName}`, file);
        if (!error) {
          newFiles.push({ id: Date.now() + Math.random(), name: file.name, type: getExt(file.name), size: formatSize(file.size), folder: selectedFolder, uploadedBy: profile.id, date: new Date().toISOString().split("T")[0], allowedRoles: viewRoles, downloadRoles: dlRoles, path: `${selectedFolder}/${fileName}` });
          success++;
        }
      } catch (err) { console.error(err); }
    }
    for (const f of newFiles) {
      await supabase.from("fichiers").insert({
        name: f.name,
        path: f.path,
        folder: f.folder,
        size: f.size,
        type: f.type,
        uploaded_by: f.uploadedBy,
        allowed_roles: JSON.stringify(f.allowedRoles),
        download_roles: JSON.stringify(f.downloadRoles),
        date: f.date,
      });
    }
    
    notify(`✅ ${success} fichier(s) uploadé(s) !`);
    setUploading(false);
    e.target.value = "";
    };

  return (
    <div>
      {/* Tabs actifs / corbeille / archive admin */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setView("actifs")} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid #e2e8f0", background: view === "actifs" ? "#1e3a5f" : "#fff", color: view === "actifs" ? "#fff" : "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>🗂️ Fichiers ({files.length})</button>
          <button onClick={() => setView("corbeille")} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid #e2e8f0", background: view === "corbeille" ? "#1e3a5f" : "#fff", color: view === "corbeille" ? "#fff" : "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>🗑️ Corbeille ({filesCorbeille.length})</button>
          {isAdmin && <button onClick={() => setView("archive")} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid #dc262644", background: view === "archive" ? "#dc2626" : "#fef2f2", color: view === "archive" ? "#fff" : "#dc2626", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>🔐 Archive Admin ({adminArchive.length})</button>}
        </div>
        {view === "corbeille" && filesCorbeille.length > 0 && isAdmin && (
          <Btn variant="danger" small onClick={async () => {
            for (const f of filesCorbeille) { if (f.path) await supabase.storage.from("central-fichiers").remove([f.path]); }
            setFilesCorbeille([]);
            notify("Corbeille vidée", "error");
          }}>🗑️ Vider définitivement</Btn>
        )}
      </div>

      {/* Archive secrète Admin */}
      {view === "archive" && isAdmin && (
        <div>
          {adminArchive.length === 0 ? (
            <Card style={{ textAlign: "center", padding: 40 }}><p style={{ fontSize: 40, marginBottom: 8 }}>🔐</p><p style={{ color: "#94a3b8", fontSize: 16 }}>Aucun fichier dans l'archive</p></Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {adminArchive.map(f => {
                const fi = FILE_ICONS[getExt(f.name)] || FILE_ICONS.other;
                return (
                  <Card key={f.id + "_arch"} style={{ padding: "14px 18px", border: f.definitivelyDeleted ? "1px solid #fecaca" : "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: fi.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{fi.icon}</div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{f.name}</p>
                        <p style={{ margin: "3px 0 0", fontSize: 12, color: "#64748b" }}>{f.folder} · {f.size} · Supprimé le {f.deletedAt?.split("T")[0]}</p>
                        {f.definitivelyDeleted && <Badge label="❌ Supprimé définitivement par l'utilisateur" color="#ef4444" />}
                        {!f.definitivelyDeleted && <Badge label="🗑️ En corbeille utilisateur" color="#f59e0b" />}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Btn small variant="primary" onClick={async () => {
                          if (f.path) {
                            const { data } = await supabase.storage.from("central-fichiers").createSignedUrl(f.path, 60);
                            if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                          }
                        }}>⬇️ Télécharger</Btn>
                        <Btn small variant="success" onClick={() => {
                          setFiles([{ ...f, definitivelyDeleted: false }, ...files]);
                          setAdminArchive(adminArchive.filter(x => x.id !== f.id));
                          setFilesCorbeille(filesCorbeille.filter(x => x.id !== f.id));
                          notify("Fichier restauré ✅");
                        }}>↩️ Restaurer</Btn>
                        <Btn small variant="danger" onClick={async () => {
                          if (f.path) await supabase.storage.from("central-fichiers").remove([f.path]);
                          setAdminArchive(adminArchive.filter(x => x.id !== f.id));
                          setFilesCorbeille(filesCorbeille.filter(x => x.id !== f.id));
                          notify("Supprimé définitivement", "error");
                        }}>❌ Supprimer</Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Corbeille view */}
      {view === "corbeille" && (
        <div>
          {filesCorbeille.length === 0 ? (
            <Card style={{ textAlign: "center", padding: 40 }}><p style={{ fontSize: 40, marginBottom: 8 }}>🗑️</p><p style={{ color: "#94a3b8", fontSize: 16 }}>Corbeille vide</p></Card>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
              {filesCorbeille.map(f => {
                const fi = FILE_ICONS[getExt(f.name)] || FILE_ICONS.other;
                return (
                  <Card key={f.id} style={{ padding: "16px 18px", opacity: 0.8 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 10, background: fi.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{fi.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 13, wordBreak: "break-word" }}>{f.name}</p>
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>{f.folder} · {f.size} · {f.date}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn small variant="success" onClick={() => { setFiles([f, ...files]); setFilesCorbeille(filesCorbeille.filter(x => x.id !== f.id)); notify("Restauré ✅"); }}>↩️ Restaurer</Btn>
                      <Btn small variant="danger" onClick={async () => {
                        if (!isAdmin) {
                          // Garder une trace dans l'archive admin même après suppression définitive
                          setAdminArchive(prev => [...prev.filter(x => x.id !== f.id), { ...f, definitivelyDeleted: true, deletedBy: profile.id, deletedAt: new Date().toISOString() }]);
                        }
                        if (f.path && isAdmin) await supabase.storage.from("central-fichiers").remove([f.path]);
                        setFilesCorbeille(filesCorbeille.filter(x => x.id !== f.id));
                        notify("Supprimé définitivement", "error");
                      }}>❌ Supprimer</Btn>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === "actifs" && <>
     <div style={{ marginBottom: 12 }}>
  <div style={{ position: "relative" }}>
    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher un fichier..." style={{ width: "100%", padding: "10px 16px", borderRadius: 10, border: "1px solid #d1d5db", fontSize: 14, outline: "none", background: "#fafafa" }} />
    {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#94a3b8" }}>✕</button>}
  </div>
</div>
      <div style={{ display: "flex", gap: 20 }}>
        <div style={{ width: 200, flexShrink: 0 }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#374151" }}>📁 Dossiers</p>
            </div>
            {["Tous", ...FOLDERS].map(f => {
              const count = f === "Tous" ? accessible.length : accessible.filter(x => x.folder === f).length;
              return (
                <button key={f} onClick={() => setFolder(f)} style={{ width: "100%", padding: "10px 16px", border: "none", background: folder === f ? "#eff6ff" : "transparent", color: folder === f ? "#2563eb" : "#374151", fontWeight: folder === f ? 700 : 500, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderLeft: folder === f ? "3px solid #2563eb" : "3px solid transparent" }}>
                  <span>{f === "Tous" ? "📂 Tous" : "📁 " + f}</span>
                  <span style={{ background: "#e5e7eb", borderRadius: 20, padding: "1px 8px", fontSize: 11 }}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1 }}>

      <Card style={{ marginBottom: 20, background: "#f8fafc", border: "2px dashed #e2e8f0" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 15 }}>☁️ Uploader un fichier</p>
              <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>Word, Excel, PDF, PowerPoint... (max 50MB)</p>
            </div>
            <Select value={selectedFolder} onChange={setSelectedFolder} options={FOLDERS.map(f => ({ value: f, label: f }))} />
            <div style={{ display: "flex", gap: 8 }}>
              <input ref={fileInputRef} type="file" onChange={handleFileUpload} style={{ display: "none" }} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg" multiple />
              <input ref={folderInputRef} type="file" onChange={handleFileUpload} style={{ display: "none" }} webkitdirectory="" />
              <Btn onClick={() => fileInputRef.current?.click()} disabled={uploading}>{uploading ? "⏳ Upload..." : "📄 Fichiers"}</Btn>
              <Btn variant="secondary" onClick={() => folderInputRef.current?.click()} disabled={uploading}>📁 Dossier</Btn>
            </div>
          </div>

          {isManager && (
            <div style={{ background: "#fff", borderRadius: 10, padding: 16, border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <RoleCheckboxes label="👁️ Qui peut VOIR ce fichier ?" selected={viewRoles} onChange={setViewRoles} color="#2563eb" />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <RoleCheckboxes label="⬇️ Qui peut TÉLÉCHARGER ?" selected={dlRoles} onChange={setDlRoles} color="#059669" />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <p style={{ fontSize: 40, marginBottom: 8 }}>📂</p>
          <p style={{ color: "#94a3b8", fontSize: 16 }}>Aucun fichier dans ce dossier</p>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {filtered.map(f => {
            const fi = FILE_ICONS[getExt(f.name)] || FILE_ICONS.other;
            const canDl = canDownload(f);
            return (
              <Card key={f.id} style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: fi.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{fi.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 13, wordBreak: "break-word" }}>{f.name}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>{f.folder} · {f.size} · {f.date}</p>
                    <div style={{ marginTop: 6 }}>
                      {canDl ? <Badge label="⬇️ Téléchargeable" color="#22c55e" /> : <Badge label="👁️ Lecture seule" color="#f59e0b" />}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {canDl ? (
                    <><Btn small variant="primary" onClick={async () => {
                      if (f.path) {
                        const { data } = await supabase.storage.from("central-fichiers").createSignedUrl(f.path, 60);
                        if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                      }
                    }}>⬇ Télécharger</Btn>
                    <Btn small variant="secondary" onClick={async () => {
                      if (f.path) {
                        const { data } = await supabase.storage.from("central-fichiers").createSignedUrl(f.path, 300);
                        if (data?.signedUrl) window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(data.signedUrl)}`, "_blank");
                      }
                    }}>👁️ Google</Btn>
                    <Btn small variant="secondary" onClick={async () => {
                      if (f.path) {
                        const { data } = await supabase.storage.from("central-fichiers").createSignedUrl(f.path, 300);
                        if (data?.signedUrl) window.open(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(data.signedUrl)}`, "_blank");
                      }
                    }}>📎 Office</Btn></>
                  ) : (
                    <span style={{ fontSize: 12, color: "#94a3b8", padding: "5px 10px", background: "#f1f5f9", borderRadius: 8 }}>🔒 Restreint</span>
                  )}
                  {(isAdmin || f.uploadedBy === profile.id) && (
                    <Btn small variant="ghost" onClick={() => {
                      if (!isAdmin) {
                        // Sauvegarder dans la corbeille secrète admin
                        setAdminArchive(prev => [...prev, { ...f, deletedBy: profile.id, deletedAt: new Date().toISOString() }]);
                      }
                      setFilesCorbeille([...filesCorbeille, f]);
                      setFiles(files.filter(x => x.id !== f.id));
                      notify("Mis à la corbeille 🗑️", "warning");
                    }}>🗑</Btn>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </div>
      </div>
      </>}
    </div>
  );
}

// ─── TÂCHES ───────────────────────────────────────────────────────────────────
function TasksPage({ tasks, setTasks, profiles, profile, isManager, notify, addLog }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: "", assignedTo: profile.id, priority: "moyenne", due: "" });
  const myTasks = isManager ? tasks : tasks.filter(t => t.assignedTo === profile.id);
  const columns = ["à faire", "en cours", "terminé"];
  const teamProfiles = profiles.filter(u => u.active && u.role !== "admin");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        {isManager && <Btn onClick={() => setShowModal(true)}>+ Nouvelle tâche</Btn>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {columns.map(col => (
          <div key={col}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: TASK_STATUS_COLORS[col] }} />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{col.charAt(0).toUpperCase() + col.slice(1)}</h3>
              <span style={{ marginLeft: "auto", background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>{myTasks.filter(t => t.status === col).length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {myTasks.filter(t => t.status === col).map(t => {
                const assignee = profiles.find(u => u.id === t.assignedTo);
                return (
                  <Card key={t.id} style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <Badge label={t.priority} color={PRIORITY_COLORS[t.priority]} />
                      {isManager && <button onClick={() => { setTasks(tasks.filter(x => x.id !== t.id)); notify("Supprimé", "error"); }} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }}>🗑</button>}
                    </div>
                    <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 14 }}>{t.title}</p>
                    {t.due && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#64748b" }}>📅 {t.due}</p>}
                    {assignee && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Avatar user={assignee} size={20} /><span style={{ fontSize: 12, color: "#64748b" }}>{assignee.name?.split(" ")[0]}</span></div>}
                    <div style={{ display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap" }}>
                      {columns.filter(c => c !== col).map(c => (<Btn key={c} small variant="secondary" onClick={() => { setTasks(tasks.map(x => x.id === t.id ? { ...x, status: c } : x)); notify("Statut mis à jour"); addLog("🔄 Tâche mise à jour", t.title + " → " + c, profile); }} style={{ fontSize: 11 }}>→ {c}</Btn>))}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Nouvelle tâche">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Titre" value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="Description de la tâche" required />
          <Select label="Assigner à" value={form.assignedTo} onChange={v => setForm({ ...form, assignedTo: v })} options={teamProfiles.map(u => ({ value: u.id, label: u.name }))} />
          <Select label="Priorité" value={form.priority} onChange={v => setForm({ ...form, priority: v })} options={[{ value: "haute", label: "🔴 Haute" }, { value: "moyenne", label: "🟡 Moyenne" }, { value: "basse", label: "🟢 Basse" }]} />
          <Input label="Échéance" type="date" value={form.due} onChange={v => setForm({ ...form, due: v })} />
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={() => { if (!form.title) return; setTasks([...tasks, { id: tasks.length + 1, ...form, status: "à faire" }]); setShowModal(false); setForm({ title: "", assignedTo: profile.id, priority: "moyenne", due: "" }); notify("Tâche créée !"); }} style={{ flex: 1 }}>Créer</Btn>
            <Btn variant="secondary" onClick={() => setShowModal(false)}>Annuler</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── PROFIL ───────────────────────────────────────────────────────────────────
function ProfilPage({ profile, updateProfile, notify }) {
  const [form, setForm] = useState({ name: profile.name, email: profile.email, poste: profile.poste || "", phone: profile.phone || "", photo: profile.photo || "", color: profile.color || "#2563eb" });
  const [previewError, setPreviewError] = useState(false);

  const handleSave = async () => {
    await updateProfile({ ...profile, ...form, avatar: getInitials(form.name) });
    notify("Profil mis à jour ✅");
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid #f1f5f9" }}>
          <div>
            {form.photo && !previewError ? <img src={form.photo} alt="profil" onError={() => setPreviewError(true)} style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "3px solid #e2e8f0" }} />
              : <div style={{ width: 80, height: 80, borderRadius: "50%", background: form.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700 }}>{getInitials(form.name)}</div>}
          </div>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700 }}>{profile.name}</h3>
            <p style={{ margin: "0 0 8px", color: "#64748b", fontSize: 14 }}>{profile.poste || ROLES_CONFIG[profile.role]?.label}</p>
            <Badge label={ROLES_CONFIG[profile.role]?.label || profile.role} color={ROLES_CONFIG[profile.role]?.color || "#6b7280"} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, border: "1px solid #e2e8f0" }}>
            <p style={{ margin: "0 0 10px", fontWeight: 600, fontSize: 14 }}>🤳 Photo de profil</p>
            <Input label="Lien URL" value={form.photo} onChange={v => { setForm({ ...form, photo: v }); setPreviewError(false); }} placeholder="https://exemple.com/photo.jpg" />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>🎨 Couleur avatar</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{COLORS_AVATAR.map(c => (<div key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: 36, height: 36, borderRadius: "50%", background: c, cursor: "pointer", border: form.color === c ? "3px solid #0f172a" : "3px solid transparent" }} />))}</div>
          </div>
          <Input label="👤 Nom complet" value={form.name} onChange={v => setForm({ ...form, name: v })} />
          <Input label="📧 Email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
          <Input label="💼 Poste / Fonction" value={form.poste} onChange={v => setForm({ ...form, poste: v })} placeholder="ex: Chargé de clientèle" />
          <Input label="📱 Téléphone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} placeholder="+33 6 00 00 00 00" />
          <Btn onClick={handleSave} style={{ marginTop: 8 }}>💾 Enregistrer</Btn>
        </div>
      </Card>
    </div>
  );
}

// ─── UTILISATEURS ─────────────────────────────────────────────────────────────
function UsersPage({ profiles, setProfiles, profile, notify, isAdmin }) {
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "employee", color: "#2563eb", poste: "", phone: "" });

  const openEdit = (u) => { setForm({ name: u.name, email: u.email, role: u.role, color: u.color, poste: u.poste || "", phone: u.phone || "" }); setEditUser(u); setShowModal(true); };

  const save = async () => {
    if (!form.name) return;
    const updated = { ...editUser, ...form, avatar: getInitials(form.name) };
    await supabase.from("profiles").upsert(updated);
    setProfiles(profiles.map(u => u.id === editUser.id ? updated : u));
    notify("Mis à jour ✅"); setShowModal(false);
  };

  const toggleActive = async (u) => {
    const updated = { ...u, active: !u.active };
    await supabase.from("profiles").upsert(updated);
    setProfiles(profiles.map(x => x.id === u.id ? updated : x));
    notify("Statut mis à jour");
  };

  // Cacher l'admin de la liste
  const visibleProfiles = profiles.filter(u => u.role !== "admin");

  // Rôles disponibles selon qui est connecté
  const roleOptions = isAdmin
    ? [{ value: "managervp", label: "🎯 Manager VP" }, { value: "manager", label: "👥 Manager" }, { value: "employee", label: "👤 Employé" }]
    : [{ value: "manager", label: "👥 Manager" }, { value: "employee", label: "👤 Employé" }];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
        {visibleProfiles.map(u => (
          <Card key={u.id} style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
              <Avatar user={u} size={52} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{u.name}</h4>
                  {u.id === profile.id && <Badge label="Vous" color="#2563eb" />}
                </div>
                <p style={{ margin: "0 0 4px", fontSize: 12, color: "#64748b" }}>{u.poste || ROLES_CONFIG[u.role]?.label}</p>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <Badge label={ROLES_CONFIG[u.role]?.label || u.role} color={ROLES_CONFIG[u.role]?.color || "#6b7280"} />
                  <Badge label={u.active ? "✓ Actif" : "✗ Inactif"} color={u.active ? "#22c55e" : "#94a3b8"} />
                </div>
              </div>
            </div>
            <p style={{ margin: "0 0 4px", fontSize: 13, color: "#64748b" }}>📧 {u.email}</p>
            {u.phone && <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>📱 {u.phone}</p>}
            {u.id !== profile.id && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                <Btn small variant="secondary" onClick={() => openEdit(u)}>✏️ Modifier</Btn>
                <Btn small variant={u.active ? "danger" : "success"} onClick={() => toggleActive(u)}>{u.active ? "Désactiver" : "Activer"}</Btn>
              </div>
            )}
          </Card>
        ))}
      </div>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Modifier l'employé">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nom complet" value={form.name} onChange={v => setForm({ ...form, name: v })} required placeholder="Prénom Nom" />
          <Input label="Poste / Fonction" value={form.poste} onChange={v => setForm({ ...form, poste: v })} placeholder="ex: Chargé de clientèle" />
          <Input label="Téléphone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} placeholder="+33 6 00 00 00 00" />
          <Select label="Rôle" value={form.role} onChange={v => setForm({ ...form, role: v })} options={roleOptions} />
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8 }}>Couleur avatar</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{COLORS_AVATAR.map(c => (<div key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: 30, height: 30, borderRadius: "50%", background: c, cursor: "pointer", border: form.color === c ? "3px solid #0f172a" : "3px solid transparent" }} />))}</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}><Btn onClick={save} style={{ flex: 1 }}>Enregistrer</Btn><Btn variant="secondary" onClick={() => setShowModal(false)}>Annuler</Btn></div>
        </div>
      </Modal>
    </div>
  );
}

function ChatPage({ messages, setMessages, profile, isManager, notify }) {
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const envoyerMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    const msg = {
      user_id: profile.id,
      user_name: profile.name,
      user_role: profile.role,
      user_color: profile.color,
      content: newMessage.trim(),
    };
    const { data } = await supabase.from("messages").insert(msg).select().single();
    if (data) setMessages([...messages, data]);
    setNewMessage("");
    setSending(false);
  };

  const supprimerMessage = async (id) => {
    await supabase.from("messages").delete().eq("id", id);
    setMessages(messages.filter(m => m.id !== id));
    notify("Message supprimé", "error");
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const ROLES_CONFIG = { admin: { label: "Admin", color: "#2563eb" }, managervp: { label: "Manager VP", color: "#dc2626" }, manager: { label: "Manager", color: "#7c3aed" }, employee: { label: "Employé", color: "#059669" } };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 160px)" }}>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 16 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <p style={{ fontSize: 40, marginBottom: 8 }}>💬</p>
            <p style={{ color: "#94a3b8", fontSize: 16 }}>Aucun message pour l'instant</p>
            <p style={{ color: "#cbd5e1", fontSize: 13 }}>Soyez le premier à écrire !</p>
          </div>
        )}
        {messages.map(m => {
          const isMe = m.user_id === profile.id;
          const roleConfig = ROLES_CONFIG[m.user_role] || { label: m.user_role, color: "#6b7280" };
          return (
            <div key={m.id} style={{ display: "flex", gap: 10, flexDirection: isMe ? "row-reverse" : "row", alignItems: "flex-start" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: m.user_color || "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {m.user_name?.slice(0, 2).toUpperCase()}
              </div>
              <div style={{ maxWidth: "70%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexDirection: isMe ? "row-reverse" : "row" }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{isMe ? "Vous" : m.user_name}</span>
                  <span style={{ fontSize: 11, color: roleConfig.color, fontWeight: 600 }}>{roleConfig.label}</span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatDate(m.created_at)}</span>
                </div>
                <div style={{ background: isMe ? "#1e3a5f" : "#f1f5f9", color: isMe ? "#fff" : "#0f172a", padding: "10px 14px", borderRadius: isMe ? "12px 12px 0 12px" : "12px 12px 12px 0", fontSize: 14, lineHeight: 1.5 }}>
                  {m.content}
                </div>
                {(isManager || isMe) && (
                  <button onClick={() => supprimerMessage(m.id)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 11, marginTop: 4, float: isMe ? "left" : "right" }}>🗑 Supprimer</button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ display: "flex", gap: 10, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
        <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && envoyerMessage()}
          placeholder="Écrivez votre message... (Entrée pour envoyer)"
          style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
        <button onClick={envoyerMessage} disabled={sending || !newMessage.trim()}
          style={{ padding: "12px 20px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer", opacity: sending ? 0.5 : 1 }}>
          {sending ? "..." : "Envoyer 📤"}
        </button>
      </div>
    </div>
  );
}
function AnnoncesPage({ annonce, setAnnonce, annonceActive, setAnnonceActive, profile, notify }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const envoyerAnnonce = async () => {
    if (!message.trim()) return;
    setSending(true);
    // Désactiver l'ancienne annonce
    await supabase.from("annonces").update({ active: false }).eq("active", true);
    // Créer la nouvelle
    const { data } = await supabase.from("annonces").insert({ message, active: true, created_by: profile.id }).select().single();
    if (data) setAnnonceActive(data);
    setMessage("");
    setSending(false);
    notify("Annonce envoyée à toute l'équipe ! 📢");
  };

  const supprimerAnnonce = async () => {
    await supabase.from("annonces").update({ active: false }).eq("active", true);
    setAnnonceActive(null);
    notify("Annonce supprimée", "error");
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "24px", marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>📢 Envoyer une annonce à l'équipe</h3>
        <textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Écrivez votre message ici... ex: Réunion vendredi à 10h"
          style={{ width: "100%", padding: "12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, minHeight: 100, resize: "vertical", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
        <button onClick={envoyerAnnonce} disabled={sending || !message.trim()}
          style={{ marginTop: 12, padding: "10px 24px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer", opacity: sending ? 0.5 : 1 }}>
          {sending ? "Envoi..." : "📢 Envoyer l'annonce"}
        </button>
      </div>

      {annonceActive && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", padding: "24px" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>📌 Annonce active</h3>
          <div style={{ background: "#1e3a5f", color: "#fff", padding: "16px 20px", borderRadius: 10, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{annonceActive.message}</p>
          </div>
          <button onClick={supprimerAnnonce}
            style={{ padding: "8px 18px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            ❌ Supprimer l'annonce
          </button>
        </div>
      )}

      {!annonceActive && (
        <div style={{ background: "#f8fafc", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px", textAlign: "center" }}>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>Aucune annonce active pour le moment</p>
        </div>
      )}
    </div>
  );
}

function RappelsPage({ rappels, setRappels, profiles, profile, isManager, notify }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ titre: "", description: "", adresse: "", date_rappel: "", assigned_to: profile.id });

  const teamProfiles = profiles.filter(u => u.role !== "admin");

  const creerRappel = async () => {
    if (!form.titre || !form.date_rappel) return;
    const { data } = await supabase.from("rappels").insert({ ...form, created_by: profile.id }).select().single();
    if (data) setRappels([...rappels, data]);
    setForm({ titre: "", description: "", adresse: "", date_rappel: "", assigned_to: profile.id });
    setShowModal(false);
    notify("Rappel créé ! 🔔");
  };

  const terminerRappel = async (id) => {
    await supabase.from("rappels").update({ done: true }).eq("id", id);
    setRappels(rappels.filter(r => r.id !== id));
    notify("Rappel terminé ✅");
  };

  const supprimerRappel = async (id) => {
    await supabase.from("rappels").delete().eq("id", id);
    setRappels(rappels.filter(r => r.id !== id));
    notify("Rappel supprimé", "error");
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();
  const isPast = (iso) => new Date(iso) < new Date();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        <button onClick={() => setShowModal(true)} style={{ padding: "9px 20px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>+ Nouveau rappel</button>
      </div>

      {rappels.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb" }}>
          <p style={{ fontSize: 40, marginBottom: 8 }}>🔔</p>
          <p style={{ color: "#94a3b8", fontSize: 16 }}>Aucun rappel pour le moment</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rappels.map(r => {
            const assignee = profiles.find(u => u.id === r.assigned_to);
            const today = isToday(r.date_rappel);
            const past = isPast(r.date_rappel);
            return (
              <div key={r.id} style={{ background: "#fff", borderRadius: 14, border: `1px solid ${today ? "#dc2626" : past ? "#f59e0b" : "#e5e7eb"}`, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{r.titre}</span>
                      {today && <span style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>🔴 Aujourd'hui</span>}
                      {past && !today && <span style={{ background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>⚠️ Passé</span>}
                    </div>
                    {r.description && <p style={{ margin: "0 0 6px", fontSize: 13, color: "#64748b" }}>{r.description}</p>}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13, color: "#64748b" }}>
                      <span>📅 {formatDate(r.date_rappel)}</span>
                      {r.adresse && <span>📍 {r.adresse}</span>}
                      {assignee && <span>👤 {assignee.name}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => terminerRappel(r.id)} style={{ padding: "6px 12px", background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>✅ Terminé</button>
                    {isManager && <button onClick={() => supprimerRappel(r.id)} style={{ padding: "6px 12px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>🗑</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, minWidth: 360, maxWidth: 500, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔔 Nouveau rappel</h3>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Titre *</label>
                <input value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} placeholder="ex: Visite client ABC" style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Détails..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Adresse</label>
                <input value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} placeholder="ex: 12 Rue de la Paix, Paris" style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Date et heure *</label>
                <input type="datetime-local" value={form.date_rappel} onChange={e => setForm({ ...form, date_rappel: e.target.value })} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none" }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>Assigner à</label>
                <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, background: "#fafafa" }}>
                  {teamProfiles.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <button onClick={creerRappel} style={{ flex: 1, padding: "10px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Créer le rappel</button>
                <button onClick={() => setShowModal(false)} style={{ padding: "10px 20px", background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HISTORIQUE ───────────────────────────────────────────────────────────────
function HistoriquePage({ historique, profiles }) {
  const [filterUser, setFilterUser] = useState("tous");
  const [filterAction, setFilterAction] = useState("tous");

  const visibleProfiles = profiles.filter(u => u.role !== "admin");
  
  const filtered = historique
    .filter(h => filterUser === "tous" || h.userId === filterUser)
    .filter(h => filterAction === "tous" || h.action.includes(filterAction));

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const roleColors = { managervp: "#dc2626", manager: "#7c3aed", employee: "#059669" };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, background: "#fafafa" }}>
          <option value="tous">👥 Tous les utilisateurs</option>
          {visibleProfiles.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, background: "#fafafa" }}>
          <option value="tous">📋 Toutes les actions</option>
          <option value="Fichier">📤 Fichiers</option>
          <option value="Devis">📋 Devis</option>
          <option value="Tâche">✅ Tâches</option>
          <option value="supprimé">🗑️ Suppressions</option>
        </select>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>{filtered.length} action{filtered.length > 1 ? "s" : ""}</span>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <p style={{ fontSize: 40, marginBottom: 8 }}>📜</p>
          <p style={{ color: "#94a3b8", fontSize: 16 }}>Aucune activité pour l'instant</p>
          <p style={{ color: "#cbd5e1", fontSize: 13 }}>Les actions de l'équipe apparaîtront ici</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(h => {
            const user = profiles.find(u => u.id === h.userId);
            const color = roleColors[h.userRole] || "#6b7280";
            return (
              <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {user?.avatar || h.userName?.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{h.userName}</span>
                    <span style={{ background: color + "22", color, border: "1px solid " + color + "44", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>{h.userRole === "managervp" ? "Manager VP" : h.userRole === "manager" ? "Manager" : "Employé"}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: "#374151" }}><span style={{ fontWeight: 600 }}>{h.action}</span> — {h.detail}</p>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>{formatDate(h.date)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}